/**
 * Configurações > Agenda: a jornada padrão da unidade (expediente, almoço e
 * dias de trabalho) e as categorias que cada pessoa nova recebe na primeira
 * vez que abre a agenda. Cada pessoa pode trocar a própria jornada nas
 * preferências da agenda; o que fica vazio lá segue o que está aqui.
 * Permissão `agenda:configurar` (a aba some sem ela; o backend confere).
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { lerConfiguracao, salvarConfiguracao } from "../../../services/agenda";
import { DIAS_DA_SEMANA, HORARIOS_DE_MEIA_EM_MEIA, alternarDia } from "../../Agenda/jornada";
import SeletorDeCor from "../../Agenda/SeletorDeCor";

const LIMITE_DE_CATEGORIAS = 30;
// O cron dos lembretes roda a cada minuto; mais de 10 sem rodar = parado.
const LEMBRETES_PARADOS_APOS_MIN = 10;
const horaLocal = (iso) => new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
}).format(new Date(iso));

/** Situação do cron dos lembretes, para um agendamento parado não ficar invisível. */
export function situacaoDosLembretes(ultimo, agora = Date.now()) {
  if (!ultimo) return { tom: "warning", texto: "Ainda não rodaram: o agendamento dos lembretes (cron) não está ativo." };
  const minutos = (agora - Date.parse(ultimo)) / 60000;
  if (minutos > LEMBRETES_PARADOS_APOS_MIN) return { tom: "danger", texto: `Parados desde ${horaLocal(ultimo)}: confira o agendamento (cron).` };
  return { tom: "success", texto: `Funcionando · última verificação ${horaLocal(ultimo)}.` };
}
const estiloLinha = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" };

function Hora({ id, rotulo, valor, onChange, opcional }) {
  return (
    <select id={id} aria-label={rotulo} className="input" value={valor || ""} onChange={(e) => onChange(e.target.value || null)} style={{ width: "7em" }}>
      {opcional ? <option value="">sem almoço</option> : null}
      {HORARIOS_DE_MEIA_EM_MEIA.map((h) => <option key={h} value={h}>{h}</option>)}
    </select>
  );
}

function Linha({ titulo, descricao, children }) {
  return (
    <div className="set-row">
      <div>
        <div className="set-row__lbl">{titulo}</div>
        <div className="set-row__desc">{descricao}</div>
      </div>
      <div className="set-row__control">{children}</div>
    </div>
  );
}

export default function AgendaTab() {
  const { request } = useAuth();
  const [config, setConfig] = useState(null);
  const [cores, setCores] = useState([]);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let vivo = true;
    lerConfiguracao(request)
      .then((r) => { if (vivo) { setConfig(r.configuracao); setCores(r.cores || []); } })
      .catch((e) => { if (vivo) setErro(e?.message || "Não deu para carregar a configuração da agenda."); });
    return () => { vivo = false; };
  }, [request]);

  if (erro) return <section className="set-section"><div className="set-section__body"><p style={{ color: "var(--danger-text)", fontSize: 13 }}>{erro}</p></div></section>;
  if (!config) return <section className="set-section"><div className="set-section__body" aria-busy="true">Carregando…</div></section>;

  const mudar = (campo) => (valor) => setConfig((c) => ({ ...c, [campo]: valor }));
  const categorias = config.categorias_padrao || [];
  const mudarCategoria = (indice, dados) => mudar("categorias_padrao")(categorias.map((c, i) => (i === indice ? { ...c, ...dados } : c)));

  const salvar = async () => {
    const limpas = categorias.map((c) => ({ ...c, nome: (c.nome || "").trim() })).filter((c) => c.nome);
    const resposta = await salvarConfiguracao(request, {
      jornada_inicio: config.jornada_inicio, jornada_fim: config.jornada_fim,
      almoco_inicio: config.almoco_inicio, almoco_fim: config.almoco_fim,
      dias_uteis: config.dias_uteis, categorias_padrao: limpas,
    });
    setConfig(resposta.configuracao);
  };

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Agenda</h2>
        <p className="set-section__desc">
          A jornada padrão da unidade aparece na grade da agenda de todos. Cada pessoa pode ajustar a própria nas preferências da agenda.
        </p>
      </div>
      <div className="set-section__body">
        <Linha titulo="Expediente" descricao="Início e fim do dia de trabalho.">
          <div style={estiloLinha}>
            <Hora id="agenda-inicio" rotulo="Início do expediente" valor={config.jornada_inicio} onChange={mudar("jornada_inicio")} />
            <span>às</span>
            <Hora id="agenda-fim" rotulo="Fim do expediente" valor={config.jornada_fim} onChange={mudar("jornada_fim")} />
          </div>
        </Linha>
        <Linha titulo="Almoço" descricao="Faixa que a grade mostra como pausa.">
          <div style={estiloLinha}>
            <Hora id="agenda-almoco-inicio" rotulo="Início do almoço" valor={config.almoco_inicio} onChange={mudar("almoco_inicio")} opcional />
            <span>às</span>
            <Hora id="agenda-almoco-fim" rotulo="Fim do almoço" valor={config.almoco_fim} onChange={mudar("almoco_fim")} opcional />
          </div>
        </Linha>
        <Linha titulo="Dias de trabalho" descricao="Os demais aparecem em tom mais claro na grade.">
          <div style={estiloLinha} role="group" aria-label="Dias de trabalho">
            {DIAS_DA_SEMANA.map(([dia, rotulo]) => {
              const ligado = String(config.dias_uteis || "").includes(dia);
              return (
                <button
                  key={dia}
                  type="button"
                  className={`btn btn--sm ${ligado ? "btn--primary" : "btn--ghost"}`}
                  aria-pressed={ligado}
                  onClick={() => mudar("dias_uteis")(alternarDia(config.dias_uteis, dia))}
                >
                  {rotulo}
                </button>
              );
            })}
          </div>
        </Linha>
        <Linha titulo="Categorias iniciais" descricao="O que cada pessoa recebe na primeira vez que abre a agenda. Quem já usa não é afetado.">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {categorias.map((c, indice) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={indice} style={estiloLinha}>
                <SeletorDeCor valor={c.cor} cores={cores} onChange={(cor) => mudarCategoria(indice, { cor })} rotulo={`Cor de ${c.nome || "categoria"}`} />
                <input className="input" value={c.nome} maxLength={60} onChange={(e) => mudarCategoria(indice, { nome: e.target.value })} aria-label="Nome da categoria" style={{ width: "14em" }} />
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => mudar("categorias_padrao")(categorias.filter((_, i) => i !== indice))}>Remover</button>
              </div>
            ))}
            {categorias.length < LIMITE_DE_CATEGORIAS ? (
              <div>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => mudar("categorias_padrao")([...categorias, { nome: "", cor: "azul" }])}>Adicionar categoria</button>
              </div>
            ) : null}
          </div>
        </Linha>
        <Linha titulo="Lembretes" descricao="Os avisos de lembrete das tarefas saem de um agendamento que roda a cada minuto.">
          {(() => {
            const situacao = situacaoDosLembretes(config.ultimo_processamento_minuto);
            return <span style={{ fontSize: 13, color: `var(--${situacao.tom}-text)` }}>{situacao.texto}</span>;
          })()}
        </Linha>
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 12 }}>
          <AsyncButton onClick={salvar} successMessage="Configuração da agenda salva.">Salvar</AsyncButton>
        </div>
      </div>
    </section>
  );
}
